"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { ChevronDown, ChevronRight, FolderOpen } from "lucide-react";
import { useProjectStore } from "@/stores/project-store";
import { useEpisodeStore, type Episode } from "@/stores/episode-store";
import { cn } from "@/lib/utils";

/**
 * 层级面包屑：**全部项目 › 项目 › 集 › 当前阶段（分镜 / 镜头）**。
 *
 * ## 为什么需要它
 * 2026-10-21 用户反馈：项目页顶栏只有「← 一眼看穿-全20集」——**只有项目名，没有集名**，
 * 从深链进来、或连开几集对比时，根本认不出「我现在改的是哪一集」，
 * 也没法按层级往回跳（只能一路退回项目列表再点进来）。
 *
 * ## 层级与落点（每级只回退一层，不做「一步回首页」的越级跳）
 * | 层级 | 文案 | 落点 |
 * |---|---|---|
 * | 1 | 全部项目 | `/{locale}`（项目列表） |
 * | 2 | 项目名 | `/{locale}/project/{id}/episodes`（该项目分集列表） |
 * | 3 | 集名 | 本集**同一阶段**（换集时留在分镜页，而不是被踢回剧本页） |
 * | 4 | 分镜 · N 镜 | 当前位置（`aria-current="page"`，不可点） |
 *
 * 第 3 级带下拉：直接在本层换集（一个项目几十集，每次都要退回列表再点进来，
 * 是这次反馈里最费手的一条路径）。
 *
 * ## 挂在哪
 * 挂在**项目布局的顶栏**（`project/[id]/layout.tsx`）而不是各页面正文里：
 *   · 顶栏是唯一「所有阶段页都常驻」的位置，切到剧本/角色/场景都还看得见自己在哪一集；
 *   · 正文各页首屏已经很挤（阶段规范 + 剧本完整性 + 分镜面板），再插一条等于重复。
 *
 * ## 数据来源
 * - 项目名：优先用调用方传入的 `projectTitle`（顶栏自己用 `?lite=1` 取的，2 KB），
 *   没传时回落到 `project-store`；
 * - 集名/集序：`episode-store.episodes`（**轻量接口**，只含元信息）。
 *   列表为空且确实有 episodeId 时自己补拉一次 —— 与其他页同 key 同 TTL 缓存，不翻倍请求。
 *
 * ⚠️ 刻意**不做**「项目下拉」：唯一的项目列表接口 `GET /api/projects` 是 `select *`
 * （含项目级剧本/大纲大字段），为一个下拉把整库正文拉进浏览器不划算；
 * 项目级跳转交给第 1、2 级链接。
 */
export interface EpisodeBreadcrumbProps {
  projectId: string;
  /** 项目名（顶栏用 `?lite=1` 自己取；不传则读 store）。 */
  projectTitle?: string;
  /** 当前集；为空时只渲染「全部项目 › 项目」两级（项目级页面）。 */
  episodeId?: string | null;
  /**
   * 当前阶段的路由段（`script` / `characters` / `scenes` / `props` / `storyboard` / `preview`）。
   * 换集时沿用它 —— 用户在分镜页换集，期望还停在分镜页。
   */
  stage?: string;
  /** 末级文案（如「分镜」）。缺省时不渲染末级。 */
  stageLabel?: string;
  /** 末级附注（如「42 镜」）；缺省时不渲染。 */
  stageHint?: string;
  className?: string;
}

/** 集名前面已经有「第N集 / EP001」时不再重复拼一遍序号。 */
const EPISODE_LABEL_RE = /^(第\s*\d+\s*[集话期]|EP\s*\d+|EP\d+)/i;

function episodeLabel(ep: Pick<Episode, "title" | "sequence"> | undefined | null, fallback: string): string {
  if (!ep) return fallback;
  const title = (ep.title ?? "").trim();
  if (!title) return fallback;
  return EPISODE_LABEL_RE.test(title) ? title : `第${ep.sequence}集 · ${title}`;
}

export function EpisodeBreadcrumb({
  projectId,
  projectTitle,
  episodeId,
  stage = "storyboard",
  stageLabel,
  stageHint,
  className,
}: EpisodeBreadcrumbProps) {
  const locale = useLocale();
  const t = useTranslations("common");
  const tEpisode = useTranslations("episode");

  const storeProjectTitle = useProjectStore((s) => s.project?.title ?? "");
  const allEpisodes = useEpisodeStore((s) => s.episodes);
  const fetchEpisodeMetas = useEpisodeStore((s) => s.fetchEpisodeMetas);

  /**
   * 只认**本项目**的集。
   *
   * `episode-store.episodes` 是跨页面共享的一份 state：从项目 A 切到项目 B 时，
   * 若 B 的页面还没拉过集列表，里面装的仍是 A 的集 —— 直接渲染会让换集下拉
   * 列出别人项目的集，点下去就是一个 404 深链。按 `projectId` 过滤是最省事、
   * 且不依赖「谁先拉过」的判据。
   */
  const projectEpisodes = useMemo(
    () => allEpisodes.filter((ep) => ep.projectId === projectId),
    [allEpisodes, projectId]
  );

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // 集列表缺失时补拉一次（与其他页同一份缓存，不会重复打接口）
  useEffect(() => {
    if (episodeId && projectId && projectEpisodes.length === 0) void fetchEpisodeMetas(projectId);
  }, [episodeId, projectId, projectEpisodes.length, fetchEpisodeMetas]);

  // 点外面 / Esc 收起下拉
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const orderedEpisodes = useMemo(
    () => [...projectEpisodes].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)),
    [projectEpisodes]
  );
  const currentEpisode = useMemo(
    () => orderedEpisodes.find((ep) => ep.id === episodeId),
    [orderedEpisodes, episodeId]
  );

  const base = `/${locale}`;
  const projectHref = `${base}/project/${projectId}/episodes`;
  const stageHref = (id: string) => `${base}/project/${projectId}/episodes/${id}/${stage}`;
  const resolvedProjectTitle = (projectTitle ?? "").trim() || storeProjectTitle;

  /**
   * 窄屏策略（顶栏只有 h-14、手机 390px 装不下四级）：
   *   · 第 1 级只留图标（文字 `hidden sm:inline`）—— 语义靠 title 与 aria 保留；
   *   · 项目 / 集 / 阶段各自收紧 max-width，超长一律 truncate 而不是把顶栏撑出横向滚动。
   * 所以 `max-w-*` 必须写在**各元素自己**身上，不能塞进 linkCls 再被覆盖 ——
   * Tailwind 的同类工具类谁生效取决于样式表顺序，不取决于 class 书写顺序。
   */
  const linkCls =
    "inline-flex min-w-0 items-center gap-1 rounded-md px-1.5 py-1 transition-colors hover:bg-black/[0.05] hover:text-[var(--text-primary)]";
  const sep = <ChevronRight className="h-3 w-3 shrink-0 text-[var(--text-muted)] opacity-60" aria-hidden />;

  return (
    <div
      ref={rootRef}
      data-episode-breadcrumb
      className={cn("flex min-w-0 items-center gap-0.5 text-[12px] leading-5", className)}
    >
      <nav aria-label={t("breadcrumbNav")} className="flex min-w-0 items-center gap-0.5">
        {/* 1 级：全部项目 */}
        <Link
          href={base}
          data-crumb="projects"
          title={t("allProjects")}
          className={cn(linkCls, "shrink-0 text-[var(--text-muted)]")}
        >
          <FolderOpen className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="hidden truncate sm:inline">{t("allProjects")}</span>
        </Link>
        {sep}

        {/* 2 级：项目 → 该项目分集列表 */}
        <Link
          href={projectHref}
          data-crumb="project"
          title={resolvedProjectTitle || t("navProjects")}
          className={cn(linkCls, "max-w-[110px] font-medium text-[var(--text-secondary)] sm:max-w-[200px]")}
        >
          <span className="truncate">{resolvedProjectTitle || t("navProjects")}</span>
        </Link>

        {episodeId ? (
          <>
            {sep}
            {/* 3 级：集 → 本集同一阶段；带下拉可直接换集 */}
            <div className="relative flex min-w-0 items-center">
              <button
                type="button"
                data-crumb="episode"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                title={tEpisode("switchEpisode")}
                className={cn(
                  linkCls,
                  "max-w-[150px] font-semibold text-[var(--text-primary)] sm:max-w-[260px]",
                  open && "bg-black/[0.05]"
                )}
              >
                <span className="truncate">
                  {episodeLabel(currentEpisode, tEpisode("currentEpisode"))}
                </span>
                <ChevronDown
                  className={cn(
                    "h-3 w-3 shrink-0 text-[var(--text-muted)] transition-transform",
                    open && "rotate-180"
                  )}
                  aria-hidden
                />
              </button>

              {open ? (
                <div
                  role="menu"
                  aria-label={tEpisode("switchEpisode")}
                  className="absolute left-0 top-full z-50 mt-1 max-h-80 w-72 max-w-[calc(100vw-1.5rem)] overflow-y-auto rounded-xl border border-[var(--border-subtle)] bg-white p-1 shadow-lg"
                >
                  {orderedEpisodes.length === 0 ? (
                    <p className="px-2 py-2 text-[11px] text-[var(--text-muted)]">{tEpisode("noEpisodes")}</p>
                  ) : (
                    orderedEpisodes.map((ep) => {
                      const active = ep.id === episodeId;
                      return (
                        <Link
                          key={ep.id}
                          href={stageHref(ep.id)}
                          role="menuitem"
                          onClick={() => setOpen(false)}
                          aria-current={active ? "true" : undefined}
                          className={cn(
                            "flex items-center gap-2 rounded-lg px-2 py-1.5 text-[12px] transition-colors",
                            active
                              ? "bg-primary/8 font-semibold text-primary"
                              : "text-[var(--text-secondary)] hover:bg-[var(--surface)] hover:text-[var(--text-primary)]"
                          )}
                        >
                          <span className="w-12 shrink-0 font-mono text-[10px] text-[var(--text-muted)]">
                            第{ep.sequence}集
                          </span>
                          <span className="min-w-0 flex-1 truncate">{ep.title || "—"}</span>
                        </Link>
                      );
                    })
                  )}
                </div>
              ) : null}
            </div>

            {stageLabel ? (
              <>
                {sep}
                {/* 4 级：当前位置（分镜 / 镜头）—— 不可点，aria-current 标出 */}
                <span
                  data-crumb="stage"
                  aria-current="page"
                  className="inline-flex min-w-0 max-w-[96px] shrink-0 items-center gap-1.5 rounded-md bg-primary/8 px-1.5 py-1 font-semibold text-primary sm:max-w-[240px]"
                >
                  <span className="truncate">{stageLabel}</span>
                  {stageHint ? (
                    <span className="hidden shrink-0 text-[11px] font-normal text-[var(--text-muted)] sm:inline">
                      {stageHint}
                    </span>
                  ) : null}
                </span>
              </>
            ) : null}
          </>
        ) : null}
      </nav>
    </div>
  );
}
